import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Package, ShoppingBasket, TrendingUp, Clock, Check, X, MapPin, Phone, AlertCircle, Trash2, ToggleLeft, ToggleRight, Star, UserPlus, UserX, UserCheck, BarChart3, Image as ImageIcon, Settings, Building2, Bell, RefreshCw } from 'lucide-react';
import axios from 'axios';
import AdminAnalytics from '../components/AdminAnalytics';
import BannerManagement from '../components/BannerManagement';
import SiteSettings from '../components/SiteSettings';
import CitiesManagement from '../components/CitiesManagement';
import UserManagement from '../components/UserManagement';

const AdminPanel = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    total_products: 0,
    approved_products: 0,
    pending_products: 0,
    rejected_products: 0,
    total_producers: 0,
    total_consumers: 0
  });
  const [users, setUsers] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');

  const [producerForm, setProducerForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    password: ''
  });
  const [showProducerForm, setShowProducerForm] = useState(false);
  const { API, user, siteSettings } = useContext(AuthContext);
  const navigate = useNavigate();

  // Admin notifications history
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const fetchAdminNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const res = await axios.get(`${API}/admin/notifications?limit=200`);
      setAdminNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching admin notifications:', e);
      toast.error('Erro ao carregar notificações');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const resendNotification = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/resend`);
      toast.success('Notificação reenviada');
      fetchAdminNotifications();
    } catch (e) {
      console.error('Error resending notification:', e);
      toast.error('Erro ao reenviar');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/admin/notifications/${id}`);
      toast.success('Notificação removida');
      setAdminNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) {
      console.error('Error deleting notification:', e);
      toast.error('Erro ao remover');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      navigate('/');
      return;
    }

    fetchStats();
    fetchUsers();
    fetchPendingProducts();
    fetchAllProducts();
    fetchAdminNotifications();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Você não tem permissão de administrador.');
        navigate('/');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/pending`);
      setPendingProducts(response.data);
    } catch (error) {
      console.error('Error fetching pending products:', error);
      toast.error('Erro ao carregar produtos pendentes');
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/all`);
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error fetching all products:', error);
      toast.error('Erro ao carregar todos os produtos');
    }
  };

  const handleProductApproval = async (productId, status, notes = '') => {
    try {
      await axios.post(`${API}/admin/products/approve/${productId}`, {
        product_id: productId,
        status: status,
        admin_notes: notes
      });
      
      const statusText = status === 'approved' ? 'aprovado' : 'rejeitado';
      toast.success(`Produto ${statusText} com sucesso!`);
      
      // Refresh data
      fetchStats();
      fetchPendingProducts();
      fetchAllProducts();
      setAdminNotes('');
    } catch (error) {
      console.error('Error approving product:', error);
      toast.error('Erro ao processar aprovação do produto');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o produto "${productName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Produto excluído com sucesso!');
      fetchStats();
      fetchPendingProducts();
      fetchAllProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/toggle-status`);
      const newStatus = currentStatus === 'approved' ? 'rejeitado' : 'aprovado';
      toast.success(`Status alterado para ${newStatus}!`);
      fetchStats();
      fetchAllProducts();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Erro ao alterar status do produto');
    }
  };

  const handleCreateProducer = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!producerForm.name || !producerForm.phone || !producerForm.email || !producerForm.city || !producerForm.password) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }
    
    try {
      console.log('Creating producer with data:', producerForm);
      const response = await axios.post(`${API}/admin/producers`, producerForm);
      console.log('Producer created successfully:', response.data);
      
      toast.success('Produtor criado com sucesso!');
      setShowProducerForm(false);
      setProducerForm({ name: '', phone: '', email: '', city: '', password: '' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error creating producer:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erro ao criar produtor');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, {
        is_active: !currentStatus
      });
      
      const statusText = currentStatus ? 'desativado' : 'ativado';
      toast.success(`Usuário ${statusText} com sucesso!`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleProducerFormChange = (e) => {
    let value = e.target.value;
    
    // Apply phone mask
    if (e.target.name === 'phone') {
      value = applyPhoneMask(value);
    }
    
    setProducerForm({
      ...producerForm,
      [e.target.name]: value
    });
  };

  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers.replace(/(\d{1,2})/, '($1');
    } else if (numbers.length <= 6) {
      return numbers.replace(/(\d{2})(\d{1,4})/, '($1) $2');
    } else if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{1,4})/, '($1) $2-$3');
    } else if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    
    return value;
  };

  const groupProductsByProducer = (products) => {
    return products.reduce((acc, product) => {
      const producerId = product.producer_id;
      if (!acc[producerId]) {
        acc[producerId] = {
          producer: {
            id: producerId,
            name: product.producer_name,
            phone: product.producer_phone,
            city: product.producer_city
          },
          products: []
        };
      }
      acc[producerId].products.push(product);
      return acc;
    }, {});
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { text: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      approved: { text: 'Aprovado', className: 'bg-green-100 text-green-800' },
      rejected: { text: 'Rejeitado', className: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.className}>
        {config.text}
      </Badge>
    );
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">Painel Administrativo</h1>
          <p className="text-gray-600">Gestão completa da plataforma {siteSettings?.site_name || 'Nossa Feira'}</p>
          {stats.pending_products > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <span className="text-yellow-800 font-medium">
                Você tem {stats.pending_products} produto(s) aguardando aprovação
              </span>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total de Usuários</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="admin-total-users">
                    {stats.total_users}
                  </p>
                </div>
                <Users className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Produtos Aprovados</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="admin-approved-products">
                    {stats.approved_products}
                  </p>
                </div>
                <Check className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Aguardando Aprovação</p>
                  <p className="text-3xl font-bold text-yellow-600" data-testid="admin-pending-products">
                    {stats.pending_products}
                  </p>
                </div>
                <Clock className="w-12 h-12 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Produtores Ativos</p>
                  <p className="text-3xl font-bold text-orange-600" data-testid="admin-total-producers">
                    {stats.total_producers}
                  </p>
                </div>
                <Package className="w-12 h-12 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="grid w-full h-auto md:h-9 grid-cols-2 md:grid-cols-9 gap-1">
            <TabsTrigger value="pending">
              <Clock className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Aprovar ({stats.pending_products})</span>
              <span className="sm:hidden">Aprovar</span>
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Produtos</span>
              <span className="sm:hidden">Produtos</span>
            </TabsTrigger>
            <TabsTrigger value="banners">
              <ImageIcon className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Banners</span>
              <span className="sm:hidden">Banners</span>
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Configurações</span>
              <span className="sm:hidden">Config</span>
            </TabsTrigger>
            <TabsTrigger value="cities">
              <Building2 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Cidades</span>
              <span className="sm:hidden">Cidades</span>
            </TabsTrigger>
            <TabsTrigger value="producers">
              <UserPlus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Produtores</span>
              <span className="sm:hidden">Produtores</span>
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Usuários</span>
              <span className="sm:hidden">Usuários</span>
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Notificações</span>
              <span className="sm:hidden">Notifs</span>
            </TabsTrigger>
          </TabsList>

          {/* Pending Products Tab */}
          <TabsContent value="pending">
            <Card className="card-feira">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Produtos Aguardando Aprovação
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingProducts.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-pending-products">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum produto aguardando aprovação!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingProducts.map((product) => (
                      <div key={product.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg text-gray-800">{product.name}</h3>
                              <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                              <div>
                                <span className="text-sm text-gray-500">Preço:</span>
                                <p className="font-medium text-green-600">R$ {product.price.toFixed(2)}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Quantidade:</span>
                                <p className="font-medium">{product.quantity}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Categoria:</span>
                                <p className="font-medium">{product.category}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Cadastrado em:</span>
                                <p className="font-medium">{formatDate(product.created_at)}</p>
                              </div>
                            </div>

                            <div className="mb-3">
                              <span className="text-sm text-gray-500">Descrição:</span>
                              <p className="text-gray-700">{product.description || 'Sem descrição'}</p>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                <span>{product.location}, {product.producer_city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span>{product.producer_name} - {product.producer_phone}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => handleProductApproval(product.id, 'approved')}
                            className="bg-green-500 hover:bg-green-600 text-white flex-1"
                            data-testid={`approve-product-${product.id}`}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Aprovar
                          </Button>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="destructive"
                                className="flex-1"
                                data-testid={`reject-product-${product.id}`}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Rejeitar
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Rejeitar Produto</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <p>Você está rejeitando o produto: <strong>{product.name}</strong></p>
                                <p className="text-sm text-gray-600">
                                  O produtor <strong>{product.producer_name}</strong> receberá uma notificação com o motivo da rejeição.
                                </p>
                                <div>
                                  <Label htmlFor="rejection-notes">Motivo da rejeição *</Label>
                                  <Textarea
                                    id="rejection-notes"
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    placeholder="Explique o motivo da rejeição para ajudar o produtor..."
                                    rows={3}
                                    required
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => {
                                      if (!adminNotes.trim()) {
                                        toast.error('Por favor, informe o motivo da rejeição');
                                        return;
                                      }
                                      handleProductApproval(product.id, 'rejected', adminNotes);
                                    }}
                                    variant="destructive"
                                    className="flex-1"
                                  >
                                    Confirmar Rejeição
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Products Tab */}
          <TabsContent value="products">
            <Card className="card-feira">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Todos os Produtos ({allProducts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allProducts.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum produto cadastrado ainda.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allProducts.map((product) => (
                      <div key={product.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg text-gray-800">{product.name}</h3>
                              {getStatusBadge(product.status)}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                              <div>
                                <span className="text-sm text-gray-500">Preço:</span>
                                <p className="font-medium text-green-600">R$ {product.price.toFixed(2)}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Produtor:</span>
                                <p className="font-medium">{product.producer_name}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Categoria:</span>
                                <p className="font-medium">{product.category}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Cadastrado:</span>
                                <p className="font-medium">{formatDate(product.created_at)}</p>
                              </div>
                            </div>

                            {product.total_reviews > 0 && (
                              <div className="flex items-center gap-2 mb-3">
                                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                <span className="text-sm font-medium">
                                  {product.average_rating?.toFixed(1) || '0.0'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({product.total_reviews} avaliação{product.total_reviews !== 1 ? 'ões' : ''})
                                </span>
                              </div>
                            )}

                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleToggleStatus(product.id, product.status)}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                                data-testid={`toggle-status-${product.id}`}
                              >
                                {product.status === 'approved' ? (
                                  <>
                                    <ToggleRight className="w-4 h-4 text-green-600" />
                                    Rejeitar
                                  </>
                                ) : (
                                  <>
                                    <ToggleLeft className="w-4 h-4 text-gray-400" />
                                    Aprovar
                                  </>
                                )}
                              </Button>
                              
                              <Button
                                onClick={() => handleDeleteProduct(product.id, product.name)}
                                variant="destructive"
                                size="sm"
                                className="flex items-center gap-2"
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Excluir
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Producers Tab */}
          <TabsContent value="producers">
            <div className="space-y-6">
              {/* Create Producer Form */}
              <Card className="card-feira">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl font-bold text-gray-800">
                      Gerenciar Produtores
                    </CardTitle>
                    <Button 
                      onClick={() => setShowProducerForm(!showProducerForm)}
                      className="btn-feira"
                      data-testid="toggle-producer-form"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {showProducerForm ? 'Cancelar' : 'Novo Produtor'}
                    </Button>
                  </div>
                </CardHeader>
                
                {showProducerForm && (
                  <CardContent>
                    <form onSubmit={handleCreateProducer} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="producer-name">Nome Completo *</Label>
                          <Input
                            id="producer-name"
                            name="name"
                            required
                            value={producerForm.name}
                            onChange={handleProducerFormChange}
                            className="input-feira"
                            data-testid="producer-name-input"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="producer-phone">Telefone *</Label>
                          <Input
                            id="producer-phone"
                            name="phone"
                            type="tel"
                            required
                            placeholder="(xx) xxxxx-xxxx"
                            value={producerForm.phone}
                            onChange={handleProducerFormChange}
                            className="input-feira"
                            data-testid="producer-phone-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="producer-email">E-mail *</Label>
                          <Input
                            id="producer-email"
                            name="email"
                            type="email"
                            required
                            value={producerForm.email}
                            onChange={handleProducerFormChange}
                            className="input-feira"
                            data-testid="producer-email-input"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="producer-city">Cidade *</Label>
                          <Input
                            id="producer-city"
                            name="city"
                            required
                            value={producerForm.city}
                            onChange={handleProducerFormChange}
                            className="input-feira"
                            data-testid="producer-city-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="producer-password">Senha Inicial *</Label>
                        <Input
                          id="producer-password"
                          name="password"
                          type="password"
                          required
                          value={producerForm.password}
                          onChange={handleProducerFormChange}
                          className="input-feira"
                          data-testid="producer-password-input"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="btn-feira w-full"
                        data-testid="create-producer-button"
                      >
                        Criar Produtor
                      </Button>
                    </form>
                  </CardContent>
                )}
              </Card>

              {/* Products by Producer */}
              <Card className="card-feira">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-800">
                    Produtos por Produtor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(groupProductsByProducer(allProducts)).length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Nenhum produto cadastrado ainda.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.entries(groupProductsByProducer(allProducts)).map(([producerId, data]) => (
                        <div key={producerId} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">{data.producer.name}</h3>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span>{data.producer.phone}</span>
                                <span>{data.producer.city}</span>
                                <Badge variant="outline">
                                  {data.products.length} produto{data.products.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {data.products.map((product) => (
                              <div key={product.id} className="border rounded p-3 bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium text-sm">{product.name}</h4>
                                  {getStatusBadge(product.status)}
                                </div>
                                <div className="text-xs text-gray-600 space-y-1">
                                  <div>R$ {product.price.toFixed(2)} • {product.quantity} unidades</div>
                                  <div>{product.category}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="card-feira">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Bell className="w-6 h-6" />
                  Histórico de Notificações
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAdminNotifications}
                  disabled={notificationsLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${notificationsLoading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </CardHeader>
              <CardContent>
                {notificationsLoading ? (
                  <p className="text-gray-500">Carregando...</p>
                ) : adminNotifications.length === 0 ? (
                  <p className="text-gray-500">Nenhuma notificação encontrada.</p>
                ) : (
                  <div className="space-y-3">
                    {adminNotifications.map((n) => (
                      <div key={n.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-800">{n.title}</span>
                              <Badge variant={n.is_read ? 'secondary' : 'default'}>
                                {n.is_read ? 'Lida' : 'Não lida'}
                              </Badge>
                              {n.type && (
                                <Badge variant="outline">{n.type}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{n.message}</p>
                            <div className="text-xs text-gray-500 space-y-1">
                              <div>Para: <span className="font-mono">{n.user_id}</span></div>
                              <div>Enviada por: {n.created_by_name || n.created_by}</div>
                              <div>Em: {formatDateTime(n.created_at)}</div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button variant="outline" size="sm" onClick={() => resendNotification(n.id)}>
                              Reenviar
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => deleteNotification(n.id)}>
                              Excluir
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners">
            <BannerManagement />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>

          {/* Site Settings Tab */}
          <TabsContent value="settings">
            <SiteSettings />
          </TabsContent>

          {/* Cities Management Tab */}
          <TabsContent value="cities">
            <CitiesManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;